import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type ShowcaseSummary } from "@/lib/api";
import { formatDate } from "@/lib/utils";

/**
 * Dégradés de repli, un par ambiance. Tant qu'une réalisation n'a pas de
 * photo, la carte reste habitée plutôt que vide — et la teinte rappelle le
 * type d'événement.
 */
const FALLBACK_GRADIENTS: Record<string, string> = {
  Théâtre: "linear-gradient(155deg,#0e2a3d,#123a52 40%,#2aabe2 150%)",
  "Soirée privée": "linear-gradient(155deg,#2b1c0e,#4a2f12 40%,#f2a65a 150%)",
  Concert: "linear-gradient(155deg,#161029,#2a1c4a 40%,#7c6ae8 150%)",
  Entreprise: "linear-gradient(155deg,#0e2a3d,#1b3a2e 40%,#4fd1a5 150%)",
};
const DEFAULT_GRADIENT = FALLBACK_GRADIENTS["Théâtre"]!;

export function EventStrip() {
  const { data, isLoading } = useQuery({
    queryKey: ["showcases"],
    queryFn: () => api.get<{ items: ShowcaseSummary[] }>("/catalog/showcases"),
    staleTime: 5 * 60_000,
  });

  const items = data?.items ?? [];
  if (!isLoading && items.length === 0) return null;

  return (
    <section className="container py-28">
      <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
        <h2 className="max-w-[520px] font-display text-[clamp(28px,3.4vw,40px)] font-medium">
          Quelques événements récents, pour se faire une idée du rendu en conditions réelles.
        </h2>
        <Link
          to="/realisations"
          className="whitespace-nowrap border-b border-transparent pb-1.5 text-[14.5px] text-brand transition-colors hover:border-brand"
        >
          Voir tous les événements →
        </Link>
      </div>

      {/* Défilement horizontal avec accroche : la bande déborde volontairement
          du conteneur pour venir mourir sur les bords de l'écran. */}
      <div className="scroll-strip -mx-6 flex snap-x snap-proximity gap-5 overflow-x-auto px-6 pb-5 md:-mx-10 md:px-10">
        {isLoading
          ? Array.from({ length: 4 }, (_, i) => (
              <div
                key={i}
                className="h-[300px] w-[320px] shrink-0 animate-pulse rounded-2xl bg-surface"
              />
            ))
          : items.map((item) => {
              const cover = item.images[0];
              return (
                <article
                  key={item.id}
                  className="w-[320px] shrink-0 snap-start overflow-hidden rounded-2xl border border-hairline bg-surface transition-transform duration-300 hover:-translate-y-1"
                >
                  <div
                    className="relative flex h-[210px] items-end p-4"
                    style={
                      cover
                        ? { backgroundImage: `url(${cover.url})`, backgroundSize: "cover" }
                        : { background: FALLBACK_GRADIENTS[item.tag ?? ""] ?? DEFAULT_GRADIENT }
                    }
                  >
                    {item.tag && (
                      <span className="rounded-full bg-black/35 px-2.5 py-1 text-[12.5px] tracking-wide">
                        {item.tag}
                      </span>
                    )}
                  </div>
                  <div className="px-[18px] pb-5 pt-[18px]">
                    <h3 className="mb-1 font-display text-[17px] font-medium">{item.title}</h3>
                    <p className="text-[13.5px] text-muted-foreground">
                      {item.subtitle}
                      {item.eventDate && ` — ${formatDate(item.eventDate)}`}
                    </p>
                  </div>
                </article>
              );
            })}
      </div>
    </section>
  );
}
